import { Sparkles, Check } from "lucide-react";
import useStaggerReveal from "../lib/useStaggerReveal";

/**
 * CleaningScope — "what is included" card shown to the client so they know
 * exactly what they're paying for. Used in NewBooking summary, PaymentPage,
 * Gallery, and also embedded in payment-request emails (server renders this
 * list server-side — see backend/core/mailer.py::CLEANING_SCOPE_ITEMS).
 *
 * The sub-task list mirrors the actual checklist the cleaning crew executes
 * on site (see backend/routes/bookings.py::STANDARD_CHECKLIST) plus extra
 * industry-standard housekeeping steps for short-term rentals.
 */
export const CLEANING_SCOPE_ITEMS = [
  {
    key: "bedrooms",
    label: "Quartos",
    desc: "Descanso impecável para o hóspede.",
    tasks: [
      "Camas feitas com lençóis frescos e esticados",
      "Almofadas e edredão arrumados",
      "Mesas de cabeceira e cómodas limpas",
      "Maçanetas e interruptores desinfetados",
      "Cortinados alinhados e arejados",
      "Chão aspirado e esfregado",
    ],
  },
  {
    key: "bathroom",
    label: "Casa de banho",
    desc: "Higiene profunda nos pontos críticos.",
    tasks: [
      "Sanita desinfetada por dentro e fora",
      "Lavatório e torneiras polidos",
      "Espelhos sem marcas nem salpicos",
      "Vidro do duche cristalino",
      "Azulejos e rejuntes escovados",
      "Dispensers repostos e toalhas dobradas",
      "Chão desinfetado",
    ],
  },
  {
    key: "living",
    label: "Sala",
    desc: "Ambiente pronto a receber hóspedes.",
    tasks: [
      "Sofás escovados e almofadas fofas",
      "Mesas e superfícies limpas e polidas",
      "TV, comandos e consolas desinfetados",
      "Decoração arrumada no sítio",
      "Chão aspirado e esfregado",
    ],
  },
  {
    key: "kitchen",
    label: "Cozinha",
    desc: "Zero resíduos, pronta a usar.",
    tasks: [
      "Bancadas desengorduradas",
      "Fogão e placa vitrocerâmica escovados",
      "Exaustor e filtros limpos",
      "Micro-ondas limpo por dentro e fora",
      "Frigorífico verificado e higienizado",
      "Loiça lavada e arrumada",
      "Lava-loiça e torneiras polidos",
      "Lixo trocado com novo saco",
    ],
  },
  {
    key: "vacuum",
    label: "Aspirar e esfregar",
    desc: "Todos os pisos tratados.",
    tasks: [
      "Chãos duros aspirados e esfregados",
      "Tapetes e carpetes aspirados",
      "Rodapés limpos",
      "Cantos e debaixo de mobiliário",
    ],
  },
  {
    key: "dust",
    label: "Limpar o pó e detalhes",
    desc: "Onde o olhar não chega, nós chegamos.",
    tasks: [
      "Prateleiras, molduras e candeeiros",
      "Pontos altos e topos de armários",
      "Interruptores e maçanetas desinfetados",
      "Vidros interiores sem marcas",
      "Reposição de amenities (se fornecidos)",
    ],
  },
];

export default function CleaningScope({ compact = false, testId = "cleaning-scope" }) {
  // Stagger entrance: cards fade/slide up as the section scrolls into view.
  const { ref: rootRef, visible } = useStaggerReveal();

  return (
    <div ref={rootRef}
         className={compact ? "" : "lux-card p-5 md:p-6"} data-testid={testId}>
      <div className="eyebrow flex items-center gap-2 text-[#d4af37]">
        <Sparkles size={12} /> Incluído em cada limpeza
      </div>
      {!compact && (
        <p className="text-xs text-[#a0b2af] mt-2 leading-relaxed">
          Lista completa do que a equipa executa — as mesmas tarefas da checklist
          profissional verificada pelo responsável antes de finalizar cada serviço.
        </p>
      )}
      <div className={`mt-4 grid gap-3 ${compact ? "grid-cols-2" : "md:grid-cols-2"}`}>
        {CLEANING_SCOPE_ITEMS.map((item, idx) => (
          <div key={item.key}
               className={`${compact ? "" : "rounded-sm border border-[#1a4640] bg-[#031c18]/60 p-3.5"} luxstay-scope-card ${visible ? "is-visible" : ""}`}
               style={{ transitionDelay: visible ? `${idx * 80}ms` : "0ms" }}
               data-testid={`${testId}-item-${item.key}`}>
            <div className="flex items-start gap-2">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#d4af37] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#fdfbf7]">{item.label}</div>
                {!compact && (
                  <>
                    <div className="text-[11px] text-[#a0b2af] mt-0.5 leading-relaxed">
                      {item.desc}
                    </div>
                    <ul className="mt-2 space-y-1"
                        data-testid={`${testId}-tasks-${item.key}`}>
                      {item.tasks.map((task) => (
                        <li key={task}
                            className="flex items-start gap-1.5 text-[11px] text-[#cde3df] leading-snug">
                          <Check size={10} className="text-[#d4af37] mt-[3px] shrink-0" />
                          <span>{task}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
